import React, { useEffect, useMemo, useState } from 'react';
import {
   Settings as SettingsIcon,
   Bell,
   Shield,
   User,
   Clock,
   Globe,
   CalendarDays,
   Save,
   CheckCircle2,
   Loader2,
   AlertTriangle,
   ChevronDown,
   Edit3,
   AtSign,
   Copy,
} from 'lucide-react';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const BASE_TIMEZONES = [
   'UTC',
   'America/Los_Angeles',
   'America/New_York',
   'Europe/London',
   'Europe/Paris',
   'Asia/Kolkata',
   'Asia/Singapore',
   'Australia/Sydney',
];
const API_BASE_URL = (process.env.REACT_APP_API_URL || "").replace("http://", "https://");

const Settings = ({ darkMode, user, onUserUpdate }) => {
   const browserTimezone = useMemo(() => {
      try {
         return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      } catch (err) {
         return 'UTC';
      }
   }, []);

   const initialWorkingHours = useMemo(
      () => ({
         start: user?.preferences?.workingHours?.start || '09:00',
         end: user?.preferences?.workingHours?.end || '17:00',
         timezone: user?.preferences?.workingHours?.timezone || browserTimezone,
      }),
      [user, browserTimezone]
   );

   const [workingHours, setWorkingHours] = useState(initialWorkingHours);
   const [workingDays, setWorkingDays] = useState(
      () => new Set(user?.preferences?.workingDays?.length ? user.preferences.workingDays : [1, 2, 3, 4, 5])
   );
   const [meetingDuration, setMeetingDuration] = useState(user?.preferences?.meetingDuration || 30);
   const [bufferMinutes, setBufferMinutes] = useState(
      typeof user?.preferences?.bufferMinutes === 'number' ? user.preferences.bufferMinutes : 15
   );
   const [saving, setSaving] = useState(false);
   const [status, setStatus] = useState(null);

   // Profile state
   const [username, setUsername] = useState(user?.username || '');
   const [usernameError, setUsernameError] = useState('');
   const [checkingUsername, setCheckingUsername] = useState(false);
   const [savingProfile, setSavingProfile] = useState(false);
   const [profileStatus, setProfileStatus] = useState(null);
   const [expandedSection, setExpandedSection] = useState(null);
   const [linkCopied, setLinkCopied] = useState(false);

   useEffect(() => {
      setWorkingHours({
         start: user?.preferences?.workingHours?.start || '09:00',
         end: user?.preferences?.workingHours?.end || '17:00',
         timezone: user?.preferences?.workingHours?.timezone || browserTimezone,
      });
      setWorkingDays(() => new Set(user?.preferences?.workingDays?.length ? user.preferences.workingDays : [1, 2, 3, 4, 5]));
      setMeetingDuration(user?.preferences?.meetingDuration || 30);
      setBufferMinutes(typeof user?.preferences?.bufferMinutes === 'number' ? user.preferences.bufferMinutes : 15);
   }, [user, browserTimezone]);

   const timezoneOptions = useMemo(() => {
      const unique = new Set([workingHours.timezone, browserTimezone, ...BASE_TIMEZONES]);
      return Array.from(unique);
   }, [workingHours.timezone, browserTimezone]);

   const sortedWorkingDays = useMemo(() => Array.from(workingDays).sort((a, b) => a - b), [workingDays]);

   const readableWorkingDays = useMemo(
      () => (sortedWorkingDays.length ? sortedWorkingDays.map((day) => WEEKDAYS[day]).join(', ') : 'No days selected'),
      [sortedWorkingDays]
   );

   const toggleDay = (idx) => {
      setWorkingDays((prev) => {
         const next = new Set(prev);
         if (next.has(idx)) {
            if (next.size === 1) return prev; // ensure at least one day stays active
            next.delete(idx);
         } else {
            next.add(idx);
         }
         return next;
      });
   };

   // Debounced username availability check
   useEffect(() => {
      if (!username || username === user?.username) {
         setUsernameError('');
         return;
      }
      if (username.length < 3) {
         setUsernameError('Username must be at least 3 characters');
         return;
      }
      if (!/^[a-zA-Z0-9_]+$/.test(username)) {
         setUsernameError('Only letters, numbers, and underscores allowed');
         return;
      }

      const timer = setTimeout(async () => {
         setCheckingUsername(true);
         try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/auth/check-username?username=${username}`, {
               headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();
            if (!data.available) {
               setUsernameError('Username is already taken');
            } else {
               setUsernameError('');
            }
         } catch {
            setUsernameError('');
         } finally {
            setCheckingUsername(false);
         }
      }, 500);

      return () => clearTimeout(timer);
   }, [username, user?.username]);

   // Sync username from user prop
   useEffect(() => {
      if (user?.username) setUsername(user.username);
   }, [user?.username]);

   const handleSaveProfile = async () => {
      if (usernameError || checkingUsername) return;
      setSavingProfile(true);
      setProfileStatus(null);
      try {
         const token = localStorage.getItem('token');
         const res = await fetch(`${API_BASE_URL}/auth/profile`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ username }),
         });
         const data = await res.json();
         if (!res.ok) throw new Error(data?.msg || 'Failed to save');
         setProfileStatus({ type: 'success', message: 'Profile updated!' });
         if (typeof onUserUpdate === 'function') {
            onUserUpdate((prev) => (prev ? { ...prev, username } : prev));
         }
      } catch (err) {
         setProfileStatus({ type: 'error', message: err.message });
      } finally {
         setSavingProfile(false);
      }
   };

   const handleCopySchedulerLink = async () => {
      const schedulerLink = `https://hicapy.com/schedule/${username || 'username'}`;
      try {
         await navigator.clipboard.writeText(schedulerLink);
         setLinkCopied(true);
         setTimeout(() => setLinkCopied(false), 2000);
      } catch (err) {
         console.error('Failed to copy link:', err);
      }
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      setStatus(null);

      if (workingHours.end <= workingHours.start) {
         setStatus({ type: 'error', message: 'End time must be later than start time.' });
         return;
      }

      setSaving(true);
      try {
         const token = localStorage.getItem('token');
         if (!token) {
            throw new Error('Please sign in again to save your preferences.');
         }

         const payload = {
            workingHours,
            workingDays: sortedWorkingDays,
            meetingDuration,
            bufferMinutes,
         };

         const response = await fetch(`${API_BASE_URL}/auth/preferences/scheduling`, {
            method: 'PUT',
            headers: {
               'Content-Type': 'application/json',
               Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
         });

         const data = await response.json();
         if (!response.ok) {
            throw new Error(data?.msg || 'Unable to save preferences right now.');
         }

         setStatus({ type: 'success', message: 'Scheduling preferences updated.' });

         if (typeof onUserUpdate === 'function') {
            onUserUpdate((prev) => (prev ? { ...prev, preferences: data.preferences || payload } : prev));
         }
      } catch (error) {
         setStatus({ type: 'error', message: error?.message || 'Unexpected error while saving settings.' });
      } finally {
         setSaving(false);
      }
   };

   const sections = [
      { title: 'Account', icon: User, items: ['Profile Information', 'Email Preferences', 'Subscription Plan'] },
      { title: 'Notifications', icon: Bell, items: ['Push Notifications', 'Email Digests', 'Meeting Reminders'] },
      { title: 'Privacy', icon: Shield, items: ['Data Retention', 'Recording Consent', 'Third-party Access'] },
   ];

   const cardBase = darkMode ? 'bg-slate-800/50 border-white/5' : 'bg-white border-slate-200';
   const subtleText = darkMode ? 'text-slate-300' : 'text-slate-500';

   return (
      <div className={`p-8 h-full overflow-y-auto custom-scrollbar ${darkMode ? 'text-white' : 'text-slate-900'}`}>
         <h2 className="text-2xl font-medium mb-8 flex items-center gap-3">
            <SettingsIcon className="w-8 h-8 opacity-50" />
            Settings
         </h2>

         <div className="max-w-4xl space-y-8">
            {/* Profile Information Section */}
            <div className={`p-6 rounded-3xl border ${cardBase}`}>
               <div className="flex items-center gap-2 mb-6">
                  <User className="w-5 h-5 text-secondary" />
                  <h3 className="text-xl font-semibold">Profile Information</h3>
               </div>

               {profileStatus && (
                  <div
                     className={`flex items-center gap-2 text-sm px-4 py-2 rounded-2xl border mb-4 ${profileStatus.type === 'success'
                        ? 'text-emerald-500 border-emerald-500/30 bg-emerald-500/5'
                        : 'text-rose-500 border-rose-500/30 bg-rose-500/5'
                        }`}
                  >
                     {profileStatus.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                     <span>{profileStatus.message}</span>
                  </div>
               )}

               <div className="space-y-4">
                  <div>
                     <label className="block text-sm font-medium mb-2">Email</label>
                     <div className={`px-4 py-2 rounded-2xl border ${darkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                        <span className={subtleText}>{user?.email || 'Not set'}</span>
                     </div>
                  </div>

                  <div>
                     <label className="block text-sm font-medium mb-2">
                        <div className="flex items-center gap-2">
                           <AtSign className="w-4 h-4" /> Username
                        </div>
                     </label>
                     <div className="flex items-center gap-3">
                        <div className="flex-1 relative">
                           <input
                              type="text"
                              value={username}
                              onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                              placeholder="your_username"
                              className={`w-full px-4 py-2 rounded-2xl border bg-transparent ${usernameError
                                 ? 'border-rose-500'
                                 : darkMode
                                    ? 'border-white/10'
                                    : 'border-slate-200'
                                 }`}
                           />
                           {checkingUsername && (
                              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
                           )}
                        </div>
                        <button
                           type="button"
                           onClick={handleSaveProfile}
                           disabled={savingProfile || !!usernameError || checkingUsername || username === user?.username}
                           className={`inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-medium border transition ${savingProfile || !!usernameError || checkingUsername || username === user?.username
                              ? 'opacity-50 cursor-not-allowed'
                              : darkMode
                                 ? 'bg-white text-slate-900 border-transparent'
                                 : 'bg-slate-900 text-white border-slate-900'
                              }`}
                        >
                           {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                           Save
                        </button>
                     </div>
                     {usernameError && <p className="text-sm text-rose-500 mt-2">{usernameError}</p>}
                     {!usernameError && username && username !== user?.username && !checkingUsername && (
                        <p className="text-sm text-emerald-500 mt-2 flex items-center gap-1">
                           <CheckCircle2 className="w-3 h-3" /> Username available
                        </p>
                     )}
                     
                     {/* Scheduler Link with Copy Button */}
                     <div className={`mt-4 p-3 rounded-2xl border ${darkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                        <label className={`text-xs font-medium uppercase tracking-wide ${subtleText} block mb-2`}>
                           Your public scheduling link
                        </label>
                        <div className="flex items-center gap-2">
                           <div className={`flex-1 px-3 py-2 rounded-xl border ${darkMode ? 'bg-white/5 border-white/5' : 'bg-white border-slate-100'}`}>
                              <p className={`text-sm font-mono ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                 https://hicapy.com/schedule/{username || 'username'}
                              </p>
                           </div>
                           <button
                              type="button"
                              onClick={handleCopySchedulerLink}
                              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium border transition ${
                                 linkCopied
                                    ? 'bg-emerald-500 text-white border-emerald-500'
                                    : darkMode
                                       ? 'bg-white/10 text-white border-white/10 hover:bg-white/20'
                                       : 'bg-slate-100 text-slate-900 border-slate-200 hover:bg-slate-200'
                              }`}
                           >
                              {linkCopied ? (
                                 <>
                                    <CheckCircle2 className="w-4 h-4" />
                                    Copied!
                                 </>
                              ) : (
                                 <>
                                    <Copy className="w-4 h-4" />
                                    Copy
                                 </>
                              )}
                           </button>
                        </div>
                     </div>
                  </div>
               </div>
            </div>

            <form onSubmit={handleSubmit} className={`p-6 rounded-3xl border ${cardBase}`}>
               <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
                  <div>
                     <p className="text-sm uppercase tracking-wide opacity-60">Scheduling preferences</p>
                     <h3 className="text-xl font-semibold mt-1">Working time & days</h3>
                     <p className={`text-sm mt-1 ${subtleText}`}>
                        These settings feed the public booking link and prevent meetings outside your window.
                     </p>
                  </div>
                  <button
                     type="submit"
                     disabled={saving}
                     className={`inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-medium border transition ${saving
                        ? 'opacity-70 cursor-not-allowed'
                        : darkMode
                           ? 'bg-white text-slate-900 border-transparent'
                           : 'bg-slate-900 text-white border-slate-900'
                        }`}
                  >
                     {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                     {saving ? 'Saving...' : 'Save preferences'}
                  </button>
               </div>

               {status && (
                  <div
                     className={`flex items-center gap-2 text-sm px-4 py-2 rounded-2xl border mb-6 ${status.type === 'success'
                        ? 'text-emerald-500 border-emerald-500/30 bg-emerald-500/5'
                        : 'text-rose-500 border-rose-500/30 bg-rose-500/5'
                        }`}
                  >
                     {status.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                     <span>{status.message}</span>
                  </div>
               )}

               <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-4">
                     <label className="block text-sm font-medium">Daily window</label>
                     <div className="flex items-center gap-3">
                        <div className="flex-1">
                           <p className={`text-xs uppercase ${subtleText}`}>Start</p>
                           <input
                              type="time"
                              value={workingHours.start}
                              onChange={(e) => setWorkingHours((prev) => ({ ...prev, start: e.target.value }))}
                              className={`w-full mt-1 rounded-2xl border px-4 py-2 bg-transparent ${darkMode ? 'border-white/10' : 'border-slate-200'}`}
                           />
                        </div>
                        <div className="flex-1">
                           <p className={`text-xs uppercase ${subtleText}`}>End</p>
                           <input
                              type="time"
                              value={workingHours.end}
                              onChange={(e) => setWorkingHours((prev) => ({ ...prev, end: e.target.value }))}
                              className={`w-full mt-1 rounded-2xl border px-4 py-2 bg-transparent ${darkMode ? 'border-white/10' : 'border-slate-200'}`}
                           />
                        </div>
                     </div>

                     <div>
                        <label className="block text-sm font-medium mb-1">Timezone</label>
                        <div className="relative">
                           <Globe className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 opacity-60" />
                           <select
                              value={workingHours.timezone}
                              onChange={(e) => setWorkingHours((prev) => ({ ...prev, timezone: e.target.value }))}
                              className={`w-full pl-11 pr-4 py-2 rounded-2xl border appearance-none bg-transparent ${darkMode ? 'border-white/10' : 'border-slate-200'
                                 }`}
                           >
                              {timezoneOptions.map((tz) => (
                                 <option key={tz} value={tz} className="text-slate-900">
                                    {tz}
                                 </option>
                              ))}
                           </select>
                        </div>
                        <p className={`text-xs mt-2 ${subtleText}`}>
                           Guests will see your times translated into their timezone automatically.
                        </p>
                     </div>
                  </div>

                  <div className="space-y-4">
                     <label className="block text-sm font-medium">Meeting length</label>
                     <select
                        value={meetingDuration}
                        onChange={(e) => setMeetingDuration(Number(e.target.value))}
                        className={`w-full mt-1 rounded-2xl border px-4 py-2 bg-transparent ${darkMode ? 'border-white/10' : 'border-slate-200'}`}
                     >
                        {[15, 30, 45, 60, 90].map((minutes) => (
                           <option key={minutes} value={minutes} className="text-slate-900">
                              {minutes} minutes
                           </option>
                        ))}
                     </select>

                     <label className="block text-sm font-medium">Buffer before/after</label>
                     <select
                        value={bufferMinutes}
                        onChange={(e) => setBufferMinutes(Number(e.target.value))}
                        className={`w-full mt-1 rounded-2xl border px-4 py-2 bg-transparent ${darkMode ? 'border-white/10' : 'border-slate-200'}`}
                     >
                        {[0, 5, 10, 15, 30].map((minutes) => (
                           <option key={minutes} value={minutes} className="text-slate-900">
                              {minutes === 0 ? 'No buffer' : `${minutes} minutes`}
                           </option>
                        ))}
                     </select>

                     <div className={`p-4 rounded-2xl border ${darkMode ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-slate-50'}`}>
                        <div className="flex items-center gap-2 mb-2">
                           <Clock className="w-4 h-4 opacity-60" />
                           <span className="text-sm font-medium">Preview</span>
                        </div>
                        <p className={`text-sm ${subtleText}`}>
                           Meetings last{' '}
                           <span className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{meetingDuration} minutes</span> with a{' '}
                           <span className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{bufferMinutes} minute</span> buffer between each slot.
                        </p>
                     </div>
                  </div>
               </div>

               <div className="mt-8">
                  <label className="block text-sm font-medium mb-3">Active weekdays</label>
                  <div className="flex flex-wrap gap-2">
                     {WEEKDAYS.map((label, idx) => (
                        <button
                           type="button"
                           key={label}
                           onClick={() => toggleDay(idx)}
                           className={`px-3 py-2 rounded-2xl text-sm border transition ${workingDays.has(idx)
                              ? 'bg-secondary text-white border-transparent shadow-lg shadow-secondary/30'
                              : darkMode
                                 ? 'border-white/10 text-slate-400 hover:border-white/30'
                                 : 'border-slate-200 text-slate-500 hover:border-slate-400'
                              }`}
                        >
                           {label}
                        </button>
                     ))}
                  </div>
                  <p className={`text-xs mt-2 ${subtleText}`}>
                     At least one weekday must stay active so people can still book time with you.
                  </p>

                  <div className={`mt-6 p-4 rounded-2xl border flex flex-col gap-3 ${darkMode ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-slate-50'}`}>
                     <div className="flex items-center gap-2">
                        <CalendarDays className="w-4 h-4 opacity-60" />
                        <span className="text-sm font-medium">Public summary</span>
                     </div>
                     <p className={`text-sm ${subtleText}`}>
                        Your link advertises availability {readableWorkingDays} between {workingHours.start} and {workingHours.end} (
                        {workingHours.timezone}).
                     </p>
                  </div>
               </div>
            </form>

            {sections.map((section, idx) => (
               <div key={idx} className={`p-6 rounded-3xl border ${cardBase}`}>
                  {section.title === 'Privacy' ? (
                     /* Privacy section - Opens external link */
                     <button
                        type="button"
                        onClick={() => window.open('https://www.hicapy.com/privacy', '_blank')}
                        className="w-full flex items-center justify-between cursor-pointer group"
                     >
                        <h3 className="font-medium text-lg flex items-center gap-2">
                           <section.icon size={20} className="text-secondary" />
                           {section.title}
                        </h3>
                        <span className={`text-xs px-3 py-1 rounded-full transition ${darkMode ? 'bg-white/10 text-white group-hover:bg-white/20' : 'bg-slate-100 text-slate-600 group-hover:bg-slate-200'}`}>
                           View Policy →
                        </span>
                     </button>
                  ) : (
                     /* Other sections - Expandable dropdown */
                     <>
                        <button
                           type="button"
                           onClick={() => setExpandedSection(expandedSection === section.title ? null : section.title)}
                           className="w-full flex items-center justify-between cursor-pointer"
                        >
                           <h3 className="font-medium text-lg flex items-center gap-2">
                              <section.icon size={20} className="text-secondary" />
                              {section.title}
                           </h3>
                           <ChevronDown
                              className={`w-5 h-5 transition-transform ${expandedSection === section.title ? 'rotate-180' : ''
                                 } ${subtleText}`}
                           />
                        </button>
                        {expandedSection === section.title && (
                           <div className="space-y-1 mt-4 pt-4 border-t border-white/10">
                              {section.items.map((item, i) => (
                                 <button
                                    key={i}
                                    className={`w-full text-left py-3 px-4 rounded-xl transition-colors flex justify-between items-center ${darkMode ? 'hover:bg-white/5' : 'hover:bg-slate-50'
                                       }`}
                                 >
                                    <span className="text-sm font-light">{item}</span>
                                    <span className={`text-xs ${subtleText}`}>Coming soon</span>
                                 </button>
                              ))}
                           </div>
                        )}
                     </>
                  )}
               </div>
            ))}
         </div>
      </div>
   );
};

export default Settings;